/* eslint-disable */
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import SelectPicker from 'rsuite/SelectPicker';
import { Tooltip } from '@mui/material';
import { Settings } from '@mui/icons-material';
import { BarChart3, LayoutList, Sheet, Waypoints, Utensils, Maximize, Salad, Calendar, Minimize } from 'lucide-react';
import { Button } from '@/Components/ui/button';
import FlowChart from './FlowChart/FlowChart';
import styles from './ProcessFlowPage.module.scss';
import view from '@/Images/view.png';
import issued from '@/Images/issued.svg';
import produced from '@/Images/produced.svg';
import internalDispatch from '@/Images/internal dispatch.svg';
import defaultMode from '@/Images/DefaultMode.svg';
import wastage from '@/Images/wastage.svg';
import ega from '@/Images/ega.svg'
import yeildLossActual from '@/Images/yeildLossActual.png';
import yeildLossExpected from '@/Images/yeildLossExpected.png';
import { urls } from '@/Constants';
import FilterNodes from './FilterNodes';
import Legend from '@/Components/Legend/Legend';
import DrillDownButtons from '@/Components/DrillDownButtons/DrillDownButtons';
import { useSelector } from 'react-redux';
import QRCode from 'qrcode';
import inventoryAnalysisIcon from '@/Images/inventoryAnalysis.png';
import BatchPicker from '@/Components/BatchPicker/BatchPicker';

const BUTTON_LIST = [
    { label: 'Section Details', icon: <Waypoints size={16} />, value: 'sectionDetails', placeholder: 'Select Section' },
    { label: 'Product Journey', icon: <Utensils size={16} />, value: 'productJourney', placeholder: 'Select Product' },
];

const LEGEND_LIST = [
    { label: 'Issuance', value: 'procured', img: issued, showEye: true },
    { label: 'Produced', value: 'produced', img: produced, showEye: true },
    { label: 'Dispatch', value: 'dispatch', img: internalDispatch, showEye: true },
    { label: 'Wastage', value: 'wastage', img: wastage, showEye: true },
    { label: 'EGA', value: 'ega', img: ega, showEye: true },
    { label: 'Yield Loss Actual', value: 'yieldLossActual', img: yeildLossActual, showEye: true },
    { label: 'Yield Loss Expected', value: 'yieldLossExpected', img: yeildLossExpected, showEye: true },
    { label: 'Default Mode', value: 'defaultMode', img: defaultMode, showEye: true },
];

// Helper function to filter display values by legend(s)
// Creates a mapping from legend value to possible display key patterns
const LEGEND_KEY_MAPPING = {
    'procured': ['procured', 'issuance', 'issued'],
    'produced': ['produced', 'production'],
    'dispatch': ['dispatch', 'dispatched'],
    'wastage': ['wastage', 'waste'],
    'ega': ['ega'],
    'yieldlossactual': ['yeildlossactual', 'yieldlossactual', 'yield loss actual'],
    'yieldlossexpected': ['yeildlossexpected', 'yieldlossexpected', 'yield loss expected'],
    'defaultmode': ['defaultmode', 'default mode'],
};

const filterDisplayValueByLegend = (displayValue, legendValues) => {
    if (!legendValues) return displayValue;

    const legendsToHide = Array.isArray(legendValues) ? legendValues : [legendValues];
    if (legendsToHide.length === 0) return displayValue;

    const filtered = {};

    Object.keys(displayValue || {}).forEach((key) => {
        const keyLower = key?.trim()?.toLowerCase()?.replace(/\s+/g, '');

        const shouldHide = legendsToHide.some(legendValue => {
            const legendLower = legendValue?.toLowerCase();
            const patterns = LEGEND_KEY_MAPPING[legendLower] || [legendLower];

            return patterns.some(pattern => {
                const patternNormalized = pattern.replace(/\s+/g, '');
                return keyLower.includes(patternNormalized) || patternNormalized.includes(keyLower);
            });
        });

        if (!shouldHide) {
            filtered[key] = displayValue[key];
        }
    });

    return filtered;
};

const getNodeScore = (node) => {
    const data = node?.data || {};
    let score = 0;
    const metrics = data?.stats?.displayValue || {};
    score += Object.keys(metrics).length * 10;
    if (data?.asset?.assetName) score += 5;
    if (data?.defaultMode) score += 5;
    if (data?.totalMachines > 0) score += 5;
    if (data?.pending >= 0 || data?.completed >= 0) score += 3;
    if (data?.orderId || data?.wholeApi?.procurePoSummary || data?.wholeApi?.dispatchPoSummary) score += 3;
    return score;
};

const ProcessFlowMiddleContainer = ({
    retailStores,
    retailSelectedStore,
    setRetailSelectedStore,
    outletsFulfillmentObj,
    getProgressBarColor,
    selectedRetailOrderStats,
    showOutletData,
    toggleProcessModal,
    isInternal,
    isEdit,
    initialState,
    handleNodesState,
    poActionHandler,
    setIsChanged,
    toggleImageModal,
    toggleEditNodeModal,
    toggleAlert,
    handleEdgeDelete,
    handleNodeDelete,
    selectedStore,
    state,
    level,
    toggleProcessSetting,
    BASE_STORES,
    setInitialState,
    isChanged,
    immovableAssetsList,
    handleCancel,
    handleSave,
    setProductId,
    handleFullMode,
    fullMode = false,
    selectedBatches,
    setSelectedBatches,
}) => {
    const productList = useSelector((state) => state.form.ALL_PRODUCTS.data.LIST);
    const [isLegendOpen, setIsLegendOpen] = useState(false);
    const [drillDownView, setDrillDownView] = useState({});
    const [selectedLegend, setSelectedLegend] = useState(['defaultMode']);
    const legendRef = useRef(null);

    const filteredState = useMemo(() => {
        if (!selectedLegend) return initialState;
        const legendsToHide = Array.isArray(selectedLegend) ? selectedLegend : [selectedLegend];
        const hasDefaultMode = legendsToHide.includes('defaultMode');

        return {
            ...initialState,
            nodes: initialState?.nodes?.map((node) => ({
                ...node,
                data: {
                    ...node?.data,
                    ...(hasDefaultMode && { defaultMode: null }),
                    stats: {
                        ...node?.data?.stats,
                        displayValue: filterDisplayValueByLegend(node?.data?.stats?.displayValue, selectedLegend)
                    }
                }
            }))
        };
    }, [initialState, selectedLegend]);

    const productJourneyData = useMemo(() => {
        return productList
            ?.filter((product) => product?.skuType === 'PRODUCT' || product?.skuType === 'UNPACKED_PRODUCT')
            ?.flatMap((product) =>
                product?.variants?.map((variant) => ({
                    value: product?.id,
                    label: `${product?.productName}${variant?.displayName ? ` (${variant?.displayName})` : ''}${variant?.clientVariantId ? ` (${variant?.clientVariantId})` : ''}`,
                }))
            ) || [];
    }, [productList]);

    const sectionDetailsData = useMemo(() => {
        return Object.values(
            initialState?.nodes?.reduce((acc, node) => {
                const label = node.data.sectionName ?? '';
                if (!acc[label]) {
                    acc[label] = { label, value: label };
                }
                return acc;
            }, {}) || {}
        );
    }, [initialState?.nodes]);

    const handleLegendChange = useCallback((legendValue) => {
        setSelectedLegend(legendValue);
    }, []);

    const handleDrillDownView = useCallback((obj) => {
        setSelectedLegend(['defaultMode']);
        setProductId(null);
        const data = obj?.value === 'sectionDetails' ? sectionDetailsData : productJourneyData;
        setDrillDownView({
            ...obj,
            show: true,
            placeholder: obj?.placeholder,
            selectedValue: null,
            data,
        });
    }, [sectionDetailsData, productJourneyData]);

    const handleValueDrillDownView = useCallback((obj) => {
        if (obj?.value === 'productJourney') {
            setProductId(obj?.selectedValue);
            setSelectedBatches([]); // Clear selected batches when product changes
        }
        setDrillDownView(obj);
    }, [setSelectedBatches]);

    useEffect(() => {
        if (!isLegendOpen) return;
        const handleClickOutside = (event) => {
            if (legendRef.current && !legendRef.current.contains(event.target)) {
                setIsLegendOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isLegendOpen]);

    const filterNodes = useMemo(() => {
        let nodes = initialState?.nodes || [];

        if (drillDownView?.selectedValue && drillDownView?.value === 'sectionDetails') {
            nodes = nodes.filter((node) => node?.data?.sectionName === drillDownView?.selectedValue);
        } else if (drillDownView?.selectedValue && drillDownView?.value === 'productJourney') {
            nodes = nodes.filter((node) => Object.values(node?.data?.stats?.displayValue)?.length !== 0);
        }

        if (selectedLegend) {
            // Convert to array for consistent handling
            const legendsToHide = Array.isArray(selectedLegend) ? selectedLegend : [selectedLegend];
            const hasDefaultMode = legendsToHide.includes('defaultMode');

            nodes = nodes.map((node) => ({
                ...node,
                data: {
                    ...node?.data,
                    ...(hasDefaultMode && { defaultMode: null }),
                    stats: {
                        ...node?.data?.stats,
                        displayValue: filterDisplayValueByLegend(node?.data?.stats?.displayValue, selectedLegend)
                    }
                }
            }));
        }

        return nodes.map((node) => ({ ...node, score: getNodeScore(node) }));
    }, [initialState?.nodes, drillDownView?.selectedValue, drillDownView?.value, selectedLegend, selectedBatches]);

    const filterNodeHandlers = useMemo(() => ({
        toggleProcessModal,
        poActionHandler,
        handleAssetView: async (item) => {
            try {
                const qrDataURL = await QRCode.toDataURL(item?.asset?.assetQrCode, { width: 500, height: 400 });
                toggleImageModal(true, {
                    image: qrDataURL,
                    width: '50rem',
                    height: '50rem',
                    heading: `${item?.asset?.assetName} (${item?.asset?.assetTypeName})`,
                    centerImage: true,
                });
            } catch (error) {
                console.error('QR Code generation failed:', error);
            }
        },
        handleAssetUpdate: (item) => {
            toggleEditNodeModal(true, {
                heading: item?.heading,
                sectionId: item?.sectionId,
                asset: item?.asset?.id,
                type: item?.type,
                defaultMode: item?.defaultMode,
                id: item?.id,
                nodeStore: item?.nodeStore,
            });
        },
        onDelete: handleNodeDelete,
    }), [toggleProcessModal, poActionHandler, toggleImageModal, toggleEditNodeModal, handleNodeDelete]);

    const productionAnalysisUrl = useMemo(() => {
        const dateFilter = encodeURIComponent(JSON.stringify({ from: state?.selectedDate, to: state?.selectedDate }));
        return `${urls.EXTENSION}${urls.PRODUCTION_ANALYSIS}?tab=geographyWise&timeFilter=store&dateRangeFilter=${dateFilter}`;
    }, [state?.selectedDate]);

    const operationsSummaryUrl = useMemo(() => {
        const dateFilter = encodeURIComponent(JSON.stringify({ from: state?.selectedDate, to: state?.selectedDate }));
        return `${urls.EXTENSION}${urls.AIFMS_SUMMARY_DYNAMIC}?tab=geographyWise&timeFilter=store&dateRangeFilter=${dateFilter}`;
    }, [state?.selectedDate]);

    const inventoryAnalysisUrl = useMemo(() => {
        return `${urls.EXTENSION}${urls.INV_ANALYSIS}`;
    }, []);
    const longTermProductionPlansUrl = useMemo(() => {
        return `${urls.EXTENSION}${urls.PRODUCTION_PLANS}?openModal=true`;
    }, []);

    const toggleLegend = useCallback(() => setIsLegendOpen(prev => !prev), []);

    const openProductionAnalysis = useCallback(() => window.open(productionAnalysisUrl, '_blank'), [productionAnalysisUrl]);
    const openOperationsSummary = useCallback(() => window.open(operationsSummaryUrl, '_blank'), [operationsSummaryUrl]);
    const openInventoryAnalysis = useCallback(() => window.open(inventoryAnalysisUrl, '_blank'), [inventoryAnalysisUrl]);
    const openLongTermProductionPlans = useCallback(() => window.open(longTermProductionPlansUrl, '_blank'), [longTermProductionPlansUrl]);
    const handleBack = useCallback(() => {
        if (drillDownView?.value === 'productJourney') {
            setProductId(null);
        }
        setSelectedLegend(null);
        setDrillDownView(null);
    }, [drillDownView, setProductId, selectedLegend, drillDownView]);

    const handleSettingsClick = useCallback(() => {
        toggleProcessSetting(true, {
            storeId: selectedStore,
            baseStores: BASE_STORES,
            setInitialState,
            isChanged,
            setIsChanged,
            immovableAssetsList,
        });
    }, [toggleProcessSetting, selectedStore, BASE_STORES, setInitialState, isChanged, setIsChanged, immovableAssetsList]);

    const hasRetailStats = Object.keys(selectedRetailOrderStats?.statsSummary || {}).length > 0;
    const fulfillmentPercent = Number.isNaN(outletsFulfillmentObj?.fulfillmentPercent) ? 0 : outletsFulfillmentObj?.fulfillmentPercent;
    return (
        <div className='relative overflow-auto' style={{ height: fullMode ? '90vh' : 'calc(100vh - 290px)' }}>
            {/* Bottom Right Panel - Distribution & Work Orders */}
            {!drillDownView?.show && !fullMode && (
                <div className='absolute bottom-0 right-0 flex flex-col gap-2 z-[999]'>
                    <div className='flex items-centre gap-1 w-full'>
                        <div className='flex flex-col gap-2 p-2' style={{ backgroundColor: 'rgba(79, 56, 100, 0.75)' }}>
                            <div className='flex flex-col gap-2'>
                                <span className='text-white'>Distribution</span>
                                <SelectPicker
                                    data={retailStores}
                                    value={retailSelectedStore}
                                    onChange={setRetailSelectedStore}
                                    placeholder='Select Outlets'
                                    placement='left'
                                    className='w-full'
                                    cleanable={false}
                                />
                            </div>
                            <div className={styles['progress-bar']}>
                                <div
                                    className={styles['progress-bar-fill']}
                                    style={{
                                        width: `${fulfillmentPercent}%`,
                                        backgroundColor: getProgressBarColor(fulfillmentPercent),
                                    }}
                                />
                                <div className={styles['progress-bar-text']}>
                                    <span className={styles['label']}>% Receiving Done (WT)</span>
                                    <span className={styles['percentage']}>{fulfillmentPercent}%</span>
                                </div>
                            </div>
                        </div>
                        {hasRetailStats && (
                            <div className='flex flex-col gap-2' style={{ backgroundColor: 'rgba(37, 99, 235, 0.15)' }}>
                                <div className='h-[30px] w-[30px] flex items-center justify-center cursor-pointer' onClick={showOutletData}>
                                    <img src={view} alt='view' className='h-full w-full' />
                                </div>
                            </div>
                        )}
                    </div>
                    <div className='bg-[#f1f1f1] flex gap-2 p-4 rounded-xl'>
                        <Tooltip title='Pending Work orders'>
                            <div className='flex gap-2 items-center'>
                                <div className='w-[25px] h-[25px] rounded-full' style={{ background: 'var(--uy-bg, #facc15)' }} />
                                <p className='font-semibold text-sm'>WO Pending</p>
                            </div>
                        </Tooltip>
                        <Tooltip title='Completed Work orders'>
                            <div className='flex gap-2 items-center'>
                                <div className='w-[25px] h-[25px] rounded-full' style={{ background: 'rgb(0, 157, 0)' }} />
                                <p className='font-semibold text-sm'>WO Completed</p>
                            </div>
                        </Tooltip>
                    </div>
                </div>
            )}

            {/* Top Left Controls */}
            <div className='flex absolute flex-col gap-2 justify-start items-start gap-2 top-[0.5rem] left-[2rem] z-30'>
                <Tooltip title='Go to Production Analysis'>
                    <div onClick={openProductionAnalysis} className='px-3 py-2 bg-yellow-400 rounded-md flex items-center gap-2 cursor-pointer hover:bg-yellow-500 transition-colors shadow-md'>
                        <BarChart3 size={18} strokeWidth={1.5} />
                    </div>
                </Tooltip>
                <Tooltip title='Go to Inventory Analysis'>
                    <div onClick={openInventoryAnalysis} className='px-3 py-2 bg-yellow-400 rounded-md flex items-center gap-2 cursor-pointer hover:bg-yellow-500 transition-colors shadow-md'>
                        <img src={inventoryAnalysisIcon} className='h-4 w-4 scale-150' />
                    </div>
                </Tooltip>
                <Tooltip title='Go to Operations Summary (Beta)'>
                    <div onClick={openOperationsSummary} className='px-3 py-2 bg-yellow-400 rounded-md flex items-center gap-2 cursor-pointer hover:bg-yellow-500 transition-colors shadow-md'>
                        <Sheet size={18} strokeWidth={1.5} />
                    </div>
                </Tooltip>
                <Tooltip title='Create Long term Production Plan'>
                    <div onClick={openLongTermProductionPlans} className='px-3 py-2 bg-yellow-400 rounded-md flex items-center gap-2 cursor-pointer hover:bg-yellow-500 transition-colors shadow-md'>
                        <Calendar size={18} strokeWidth={1.5} />
                    </div>
                </Tooltip>
                <div ref={legendRef} className='relative'>
                    <div onClick={toggleLegend} className='px-3 py-2 border border-black rounded-md flex items-center gap-2 cursor-pointer shadow-md bg-white'>
                        <LayoutList size={18} strokeWidth={1.5} />
                    </div>
                    {isLegendOpen && (
                        <Legend list={LEGEND_LIST} setIsLegend={handleLegendChange} defaultValue={['defaultMode']} isClickable selectedLegend={selectedLegend} multiSelect={true} />
                    )}
                </div>
            </div>
            <div className='absolute flex justify-start items-start gap-2 top-[0.5rem] left-[5rem] z-30'>
                <DrillDownButtons
                    buttonList={BUTTON_LIST}
                    drillDownView={drillDownView}
                    setDrillDownView={handleValueDrillDownView}
                    onButtonClick={handleDrillDownView}
                    handleBack={handleBack}
                    virtulized
                    isTransparent={!drillDownView?.show}
                >
                    {drillDownView?.value === 'productJourney' && drillDownView?.selectedValue && (
                        <BatchPicker
                            dateRange={{ from: state?.selectedDate, to: state?.selectedDate }}
                            selectedBatches={selectedBatches}
                            setSelectedBatches={setSelectedBatches}
                            productId={drillDownView?.selectedValue}
                            onBatchSelect={(batch) => {
                                // Handle batch selection logic here if needed, 
                                // e.g., filtering nodes or updating state.
                                // For now, we utilize the selectedBatches state.
                            }}
                        />
                    )}
                </DrillDownButtons>
            </div>

            {/* Main Content */}
            {drillDownView?.show ? (
                <div style={{
                    height: fullMode ? '100vh' : 'calc(100vh - 350px)',
                    minHeight: '300px',
                    maxHeight: fullMode ? '100vh' : '80vh'
                }}>
                    <FilterNodes data={filterNodes} handlers={filterNodeHandlers} isProductJourney={drillDownView?.value === 'productJourney' && drillDownView?.selectedValue} />
                </div>
            ) : (
                <FlowChart
                    toggleProcessModal={toggleProcessModal}
                    isInternal={isInternal}
                    isEdit={isEdit}
                    initialState={filteredState}
                    handleNodesState={handleNodesState}
                    poActionHandler={poActionHandler}
                    setIsChanged={setIsChanged}
                    toggleImageModal={toggleImageModal}
                    toggleEditNodeModal={toggleEditNodeModal}
                    toggleAlert={toggleAlert}
                    handleEdgeDelete={handleEdgeDelete}
                    handleNodeDelete={handleNodeDelete}
                    selectedStore={selectedStore}
                    newProcessFlow={true}
                />
            )}

            {/* Top Right Settings */}
            <div className='absolute top-[0.5rem] right-[1rem] z-[999] flex gap-3 items-center'>
                {isInternal && level !== 1 && (
                    <>
                        {isChanged && (
                            <div className='animate-pulse flex gap-2'>
                                <Button variant='secondary' onClick={handleCancel}>Cancel</Button>
                                <Button className='primary-btn' onClick={handleSave}>Confirm</Button>
                            </div>
                        )}
                        <Tooltip title='Settings'>
                            <div onClick={handleSettingsClick} className='text-white w-12 h-12 bg-blue-600 rounded-full flex justify-center items-center'>
                                <Settings />
                            </div>
                        </Tooltip>
                    </>
                )}
                <Tooltip title={fullMode ? 'Exit Full Screen' : 'View Full Screen'}>
                    <div onClick={handleFullMode} className='text-white w-12 h-12 bg-teal-600 rounded-full flex justify-center items-center cursor-pointer'>
                        {fullMode ? <Minimize size={18} /> : <Maximize size={18} />}
                    </div>
                </Tooltip>
            </div>
        </div >
    );
};

export default ProcessFlowMiddleContainer;

